import cx from 'classnames';
import { PropsWithChildren, ReactElement } from 'react';

import style from './Modal.module.css';
import { ModalBody } from './ModalBody';
import { ModalFooter, ModalFooterProps } from './ModalFooter';
import { ModalHeader, ModalHeaderProps } from './ModalHeader';
import { ModalHeaderWithButton, ModalHeaderWithButtonProps } from './ModalHeaderWithButton';
import { ModalImageHeader, ModalImageHeaderProps } from './ModalImageHeader';
import { ModalMinimalHeader, ModalMinimalHeaderProps } from './ModalMinimalHeader';
import { ModalSkeleton, ModalSkeletonProps } from './ModalSkeleton';

type BaseModalProps = Omit<ModalSkeletonProps, 'children' | 'className'> &
    PropsWithChildren<{
        onClose?: () => void;
        hasCloseButton?: boolean;
        hasBottomScrollIndicator?: boolean;
        dark?: boolean;
        className?: string;
        dataTestId?: string;
    }>;

export type ModalProps = BaseModalProps &
    (DefaultHeaderProps | ImageHeaderProps | MinimalHeaderProps | WithButtonHeaderProps) &
    (DefaultFooterProps | CustomFooterProps);

// Header variants
type ComputedHeaderProps = 'onClose' | 'onPreviousButtonClick' | 'size';
type DefaultHeaderProps = {
    headerVariant?: 'default';
    image?: never;
} & Omit<ModalHeaderProps, ComputedHeaderProps>;
type ImageHeaderProps = {
    headerVariant: 'image';
} & Omit<ModalImageHeaderProps, ComputedHeaderProps>;
type MinimalHeaderProps = {
    headerVariant: 'minimal';
    title?: never;
    image?: never;
} & Omit<ModalMinimalHeaderProps, ComputedHeaderProps>;
type WithButtonHeaderProps = {
    headerVariant: 'with-button';
    image?: never;
} & Omit<ModalHeaderWithButtonProps, ComputedHeaderProps>;

function isDefaultHeader(props: ModalProps): props is BaseModalProps & DefaultHeaderProps {
    return (
        props.headerVariant !== 'image' &&
        props.headerVariant !== 'minimal' &&
        props.headerVariant !== 'with-button'
    );
}

function isImageHeader(props: ModalProps): props is BaseModalProps & ImageHeaderProps {
    return props.headerVariant === 'image';
}

function isMinimalHeader(props: ModalProps): props is BaseModalProps & MinimalHeaderProps {
    return props.headerVariant === 'minimal';
}

function isWithButtonHeader(props: ModalProps): props is BaseModalProps & WithButtonHeaderProps {
    return props.headerVariant === 'with-button';
}

// Footer variants
type DefaultFooterProps = ModalFooterProps & {
    footerVariant?: 'default';
    footer?: never;
};
type CustomFooterProps = {
    footerVariant: 'custom';
    footer?: ReactElement;
};
function isDefaultFooter(props: ModalProps): props is BaseModalProps & DefaultFooterProps {
    return props.footerVariant !== 'custom';
}

export function Modal(props: ModalProps) {
    const {
        title,
        hasCloseButton = true,
        hasBottomScrollIndicator = false,
        dark,
        className,
        children,
        onClose,
        ...modalSkeletonProps
    } = props;
    const { close } = modalSkeletonProps;

    const handleClose = () => {
        onClose?.();
        close();
    };

    const commonHeaderProps = {
        onClose: hasCloseButton ? handleClose : undefined,
    };

    return (
        <ModalSkeleton
            {...modalSkeletonProps}
            onClickOverlay={handleClose}
            className={cx(style.modal, className)}
        >
            {isDefaultHeader(props) ? (
                <ModalHeader {...commonHeaderProps} title={title} dark={dark} />
            ) : isImageHeader(props) ? (
                <ModalImageHeader
                    {...commonHeaderProps}
                    image={props.image}
                    size={modalSkeletonProps.size}
                    fit={props.fit}
                    title={props.title}
                />
            ) : isMinimalHeader(props) ? (
                <ModalMinimalHeader {...commonHeaderProps} dark={dark} />
            ) : isWithButtonHeader(props) ? (
                <ModalHeaderWithButton
                    {...commonHeaderProps}
                    title={props.title}
                    buttonProps={props.buttonProps}
                    dark={dark}
                />
            ) : null}
            <ModalBody
                className={style.modalBody}
                dark={dark}
                hasBottomScrollIndicator={hasBottomScrollIndicator}
            >
                {children}
            </ModalBody>
            {isDefaultFooter(props) ? (
                <ModalFooter
                    primaryButton={props.primaryButton}
                    secondaryButton={props.secondaryButton}
                    dark={dark}
                />
            ) : (
                props.footer
            )}
        </ModalSkeleton>
    );
}
